/**
 * 🔒 PAYWALL - Composant de blocage Premium
 * Bloque les fonctionnalités premium et incite à l'abonnement
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Crown, Sparkles, Star, Heart, Eye, Zap, X, Check } from 'lucide-react';
import axios from 'axios';

// 🔧 FIX: Utiliser l'origine courante pour heartbeats.fr pour éviter les problèmes de config
const getApiUrl = () => {
  const hostname = window.location.hostname;
  // Si on est sur heartbeats.fr, utiliser l'origine directement
  if (hostname === 'heartbeats.fr' || hostname === 'www.heartbeats.fr') {
    return window.location.origin;
  }
  // Sinon utiliser la variable d'environnement
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};

const API_URL = getApiUrl();

// Fonctionnalités bloquées sans Premium
export const PREMIUM_FEATURES = {
  superLike: {
    name: 'Super Like',
    icon: '⭐',
    description: 'Montre que tu es vraiment intéressé(e)',
    freeLimit: 1, // 1 par jour gratuit
    premiumLimit: 5 // 5 par jour
  },
  seeWhoLikesYou: {
    name: 'Voir qui t\'aime',
    icon: '💜',
    description: 'Découvre qui a liké ton profil',
    freeLimit: 0,
    premiumLimit: -1 // Illimité
  },
  unlimitedLikes: {
    name: 'Likes illimités',
    icon: '❤️',
    description: 'Like autant de profils que tu veux',
    freeLimit: 30, // 30 par jour
    premiumLimit: -1
  },
  profileBoost: {
    name: 'Boost de profil',
    icon: '🚀',
    description: 'Apparais en premier pendant 30 min',
    freeLimit: 0,
    premiumLimit: 1 // 1 par semaine
  },
  readReceipts: {
    name: 'Accusés de lecture',
    icon: '👀',
    description: 'Sache quand tes messages sont lus',
    freeLimit: 0,
    premiumLimit: -1
  },
  rewind: {
    name: 'Revenir en arrière',
    icon: '↩️',
    description: 'Annule ton dernier swipe',
    freeLimit: 0,
    premiumLimit: 5
  },
  advancedFilters: {
    name: 'Filtres avancés',
    icon: '🎯',
    description: 'Filtre par taille, style de vie...',
    freeLimit: 0,
    premiumLimit: -1
  },
  incognitoMode: {
    name: 'Mode incognito',
    icon: '👻',
    description: 'Navigue sans être vu',
    freeLimit: 0,
    premiumLimit: -1
  }
};

// Plans d'abonnement
const PREMIUM_PLANS = [
  {
    id: 'premium_monthly',
    name: 'Premium Mensuel',
    price: 9.99,
    period: 'mois',
    popular: true,
    features: ['Likes illimités', 'Voir qui t\'aime', '5 Super Likes/jour', '1 Boost/semaine']
  },
  {
    id: 'premium_yearly',
    name: 'Premium Annuel',
    price: 79.99,
    period: 'an',
    savings: '33%',
    features: ['Tout Premium Mensuel', '2 mois offerts', 'Badge Premium Gold', 'Support prioritaire']
  }
];

/**
 * 🔒 Composant Paywall Modal
 */
export const PaywallModal = ({ 
  isOpen, 
  onClose, 
  feature, 
  customerId,
  customerEmail,
  onSubscribe 
}) => {
  const [loading, setLoading] = React.useState(false);
  const [selectedPlan, setSelectedPlan] = React.useState('premium_monthly');

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      // Construire le payload avec seulement les valeurs définies
      const payload = {
        package_id: selectedPlan,
        origin_url: window.location.origin,
      };
      
      // Ajouter les champs optionnels seulement s'ils sont définis
      if (customerId) payload.customer_id = customerId;
      if (customerEmail) payload.customer_email = customerEmail;
      if (feature) payload.metadata = { source: 'paywall', feature: feature };
      
      console.log('💳 Paywall - Envoi paiement:', payload);
      
      const response = await axios.post(`${API_URL}/api/payments/checkout/create`, payload);

      console.log('💳 Paywall - Réponse:', response.data);
      
      if (response.data && response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('URL de paiement manquante');
      }
      
    } catch (error) {
      console.error('💳 Paywall - Erreur:', error);
      console.error('💳 Paywall - Détails:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const featureInfo = feature ? PREMIUM_FEATURES[feature] : null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-gradient-to-b from-[#1a1a2e] to-[#0f0f1a] rounded-3xl max-w-md w-full overflow-hidden border border-white/10"
        >
          {/* Header avec animation */}
          <div className="relative bg-gradient-to-r from-purple-600 to-pink-600 p-6 text-center overflow-hidden">
            {/* Étoiles animées */}
            <div className="absolute inset-0 overflow-hidden">
              {[...Array(20)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-1 h-1 bg-white rounded-full"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                  }}
                  animate={{
                    opacity: [0.2, 1, 0.2],
                    scale: [1, 1.5, 1]
                  }}
                  transition={{
                    duration: Math.random() * 2 + 1,
                    repeat: Infinity,
                    delay: Math.random() * 2
                  }}
                />
              ))}
            </div>
            
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
            
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="inline-block mb-3"
            >
              <Crown className="w-16 h-16 text-yellow-300 mx-auto" />
            </motion.div>
            
            <h2 className="text-2xl font-bold text-white mb-1">Passe Premium 💎</h2>
            <p className="text-white/80 text-sm">Débloque toutes les fonctionnalités</p>
          </div>

          {/* Fonctionnalité bloquée */}
          {featureInfo && (
            <div className="px-6 py-4 bg-white/5 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center text-2xl">
                  {featureInfo.icon}
                </div>
                <div>
                  <p className="text-white font-semibold">{featureInfo.name}</p>
                  <p className="text-white/60 text-sm">{featureInfo.description}</p>
                </div>
                <Lock className="w-5 h-5 text-white/40 ml-auto" />
              </div>
            </div>
          )}

          {/* Plans */}
          <div className="p-6 space-y-3">
            {PREMIUM_PLANS.map((plan) => (
              <motion.button
                key={plan.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setSelectedPlan(plan.id)}
                className={`w-full p-4 rounded-2xl border-2 transition-all text-left relative ${
                  selectedPlan === plan.id 
                    ? 'border-purple-500 bg-purple-500/10' 
                    : 'border-white/10 bg-white/5 hover:border-white/30'
                }`}
              >
                {plan.popular && (
                  <span className="absolute -top-2 right-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    POPULAIRE
                  </span>
                )}
                {plan.savings && (
                  <span className="absolute -top-2 right-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    -{plan.savings}
                  </span>
                )}
                
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-semibold">{plan.name}</span>
                  <div className="text-right">
                    <span className="text-2xl font-bold text-white">{plan.price}€</span>
                    <span className="text-white/60 text-sm">/{plan.period}</span>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {plan.features.slice(0, 2).map((f, i) => (
                    <span key={i} className="text-xs text-white/60 flex items-center gap-1">
                      <Check className="w-3 h-3 text-green-400" /> {f}
                    </span>
                  ))}
                </div>
              </motion.button>
            ))}
          </div>

          {/* CTA */}
          <div className="px-6 pb-6">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSubscribe}
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl font-bold text-white text-lg flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <span className="animate-spin">⏳</span>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Devenir Premium
                </>
              )}
            </motion.button>
            
            <p className="text-center text-white/40 text-xs mt-3">
              Annulation possible à tout moment
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * 🔒 Mini Paywall inline (pour boutons bloqués)
 */
export const PremiumLock = ({ feature, onClick, children, isPremium = false }) => {
  if (isPremium) {
    return <>{children}</>;
  }

  return (
    <div className="relative" onClick={onClick}>
      {children}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[1px] rounded-lg flex items-center justify-center cursor-pointer hover:bg-black/40 transition-colors">
        <div className="flex items-center gap-1 text-yellow-400">
          <Lock className="w-4 h-4" />
          <span className="text-xs font-semibold">Premium</span>
        </div>
      </div>
    </div>
  );
};

/**
 * 🔒 Hook pour vérifier si une fonctionnalité est débloquée
 */
export const useFeatureAccess = (feature, isPremium, dailyUsage = 0) => {
  const featureConfig = PREMIUM_FEATURES[feature];
  
  if (!featureConfig) return { hasAccess: true, remaining: -1 };
  
  if (isPremium) {
    return { 
      hasAccess: true, 
      remaining: featureConfig.premiumLimit 
    };
  }
  
  const remaining = featureConfig.freeLimit - dailyUsage;
  return {
    hasAccess: remaining > 0,
    remaining: Math.max(0, remaining),
    limit: featureConfig.freeLimit
  };
};

export default PaywallModal;
